import React, { useEffect } from "react";
import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import {
  Tv, Phone, ArrowRight, ArrowLeft, Check, ShieldCheck, Award, ClipboardCheck,
  MapPin, Wrench, CheckCircle, HelpCircle, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { SERVICES, BRANDS, getEntry } from "@/seoData";
import { gtagEvent, gtagConversion, gtagPageview } from "@/analytics";
import Seo from "@/Seo";
import { localBusinessLd, serviceLd, breadcrumbLd, faqPageLd, brandFaqs } from "@/seoSchema";
import { LONG_BLURBS } from "@/seoContent";

const WHY = [
  { icon: Award, title: "A Decade on the Bench", desc: "Years of hands-on repairs across thousands of televisions." },
  { icon: ShieldCheck, title: "Vetted Engineers", desc: "Background-checked, trained professionals you can trust." },
  { icon: ClipboardCheck, title: "Straight Talk on Cost", desc: "A clear price agreed up front — no hidden extras." },
  { icon: MapPin, title: "We Travel to You", desc: "Same-day doorstep visits right across the city." },
];

export default function SeoLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const entry = getEntry(slug);

  useEffect(() => {
    if (entry) {
      document.title = `${entry.name} in Bangalore | TV CARE`;
      window.scrollTo(0, 0);
      // Fire GA page-view (NOT an Ads conversion) on each SEO landing page load / route change.
      gtagPageview(`/${slug}`);
    }
    // Depend on slug (stable) rather than entry (fresh object every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!entry) return <Navigate to="/" replace />;

  const title = entry.type === "service" ? `${entry.name} in Bangalore` : entry.name;
  const relatedServices = SERVICES.filter((s) => s.slug !== slug).slice(0, 8);
  const relatedBrands = BRANDS.filter((b) => b.slug !== slug).slice(0, 10);

  const seoTitle = `${title} | Same-Day Doorstep Service | TV CARE`;
  const seoDescription =
    entry.blurb.length > 155 ? entry.blurb.slice(0, 152) + "…" : entry.blurb;
  const path = `/${slug}`;

  const faqs = entry.type === "brand" ? brandFaqs(entry.brand) : null;

  const jsonLdBlocks = [
    localBusinessLd(),
    serviceLd({ name: entry.name, description: entry.blurb, path }),
    breadcrumbLd(title, path),
    ...(faqs ? [faqPageLd(faqs)] : []),
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col antialiased" data-testid="seo-landing-page">
      <Seo
        title={seoTitle}
        description={seoDescription}
        path={path}
        jsonLd={jsonLdBlocks}
      />
      {/* FLOATING WHATSAPP BUTTON */}
      <a
        href="https://wa.me/918884340007?text=Hi%20TV%20CARE%2C%20I%20need%20TV%20repair%20service"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-float-btn"
        aria-label="Chat on WhatsApp"
        onClick={() => { gtagEvent("whatsapp_click", { location: "seo" }); gtagConversion("whatsapp"); }}
        className="fixed bottom-6 left-6 z-[60] flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] shadow-lg shadow-black/20 hover:scale-110 transition-transform"
      >
        <svg viewBox="0 0 32 32" className="w-7 h-7" fill="#ffffff" aria-hidden="true">
          <path d="M16.001 3.2c-7.06 0-12.8 5.74-12.8 12.8 0 2.257.59 4.46 1.71 6.4L3.2 28.8l6.57-1.72a12.74 12.74 0 0 0 6.23 1.62h.005c7.06 0 12.8-5.74 12.8-12.8s-5.74-12.7-12.8-12.7zm0 23.04h-.004a10.6 10.6 0 0 1-5.4-1.48l-.388-.23-4.003 1.05 1.068-3.9-.253-.4a10.58 10.58 0 0 1-1.62-5.64c0-5.86 4.77-10.63 10.64-10.63 2.84 0 5.51 1.11 7.52 3.12a10.56 10.56 0 0 1 3.11 7.52c0 5.87-4.77 10.6-10.64 10.6zm5.83-7.94c-.32-.16-1.89-.93-2.18-1.04-.29-.11-.5-.16-.71.16-.21.32-.82 1.04-1.005 1.25-.185.21-.37.24-.69.08-.32-.16-1.35-.5-2.57-1.59-.95-.85-1.59-1.9-1.78-2.22-.185-.32-.02-.49.14-.65.144-.143.32-.37.48-.56.16-.19.21-.32.32-.53.106-.21.053-.4-.027-.56-.08-.16-.71-1.71-.97-2.35-.256-.616-.516-.53-.71-.54l-.605-.01c-.21 0-.55.08-.84.4-.29.32-1.1 1.08-1.1 2.63s1.13 3.05 1.29 3.26c.16.21 2.22 3.39 5.38 4.75.75.32 1.34.51 1.8.66.756.24 1.444.206 1.99.125.607-.09 1.89-.773 2.156-1.52.266-.747.266-1.387.186-1.52-.08-.133-.29-.21-.61-.37z"/>
        </svg>
      </a>

      {/* HEADER */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm backdrop-blur-md bg-white/95">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="seo-logo-home">
            <div className="p-2 bg-[#0a2540] rounded-lg text-white">
              <Tv className="h-6 w-6" />
            </div>
            <div className="flex flex-col">
              <span className="font-extrabold text-xl tracking-tight text-[#0a2540] leading-none">TV CARE</span>
              <span className="text-[10px] text-slate-500 font-medium uppercase tracking-wider mt-1">Your Tv Our Care</span>
            </div>
          </Link>
          <a
            href="tel:8884340007"
            data-testid="seo-phone-link"
            onClick={() => { gtagEvent("call_click", { location: "seo-header" }); gtagConversion("call"); }}
            className="flex items-center gap-2.5 bg-[#0a2540] hover:bg-slate-800 text-white rounded-xl h-10 px-5 text-sm font-semibold shadow-sm transition-all"
          >
            <Phone className="h-4 w-4 text-[#f97316]" />
            <span className="hidden sm:inline">(888) 434-0007</span>
            <span className="sm:hidden">Call</span>
          </a>
        </div>
      </header>

      <main className="flex-1">
        {/* HERO */}
        <section className="bg-white border-b border-slate-200 py-14 sm:py-20">
          <div className="max-w-4xl mx-auto px-6 text-left">
            <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-[#0a2540] mb-6" data-testid="seo-back-home">
              <ArrowLeft className="h-4 w-4" /> Back to Home
            </Link>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0a2540]/5 rounded-full text-sm font-semibold text-[#0a2540] mb-5">
              <Wrench className="h-4 w-4 text-[#f97316]" /> Doorstep service across Bangalore
            </div>
            <h1 className="text-4xl sm:text-5xl tracking-tight font-extrabold text-[#0a2540] leading-tight mb-5" data-testid="seo-page-title">
              {title}
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-2xl">{entry.blurb}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="seo-book-btn"
                onClick={() => navigate("/?action=book")}
                className="bg-[#0a2540] hover:bg-slate-800 text-white rounded-xl h-12 px-8 text-base font-semibold shadow-md transition-all flex items-center gap-2"
              >
                Request a Visit <ArrowRight className="h-5 w-5" />
              </Button>
              <a
                href="tel:8884340007"
                onClick={() => { gtagEvent("call_click", { location: "seo-hero" }); gtagConversion("call"); }}
                className="border border-slate-300 hover:bg-slate-50 text-[#0a2540] rounded-xl h-12 px-8 text-base font-semibold transition-all flex items-center justify-center gap-2"
              >
                <Phone className="h-5 w-5 text-[#f97316]" /> Talk to an Expert
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-6 text-sm font-medium text-slate-600">
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-[#f97316]" /> We Come To You</span>
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-[#f97316]" /> No-Obligation Quote</span>
              <span className="flex items-center gap-1.5"><Check className="h-4 w-4 text-[#f97316]" /> Half-Year Warranty</span>
            </div>
          </div>
        </section>

        {/* COMMON FAULTS */}
        <section className="py-16 bg-[#f8fafc] border-b border-slate-200">
          <div className="max-w-4xl mx-auto px-6 text-left">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0a2540] tracking-tight mb-8">
              {entry.type === "locality" ? "What We Handle Locally" : "Problems We Sort Out"}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {entry.faults.map((f, i) => (
                <div key={i} className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl p-5">
                  <CheckCircle className="h-5 w-5 text-[#f97316] flex-shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-slate-700">{f}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* LONG-FORM CONTENT (SEO body copy) */}
        {LONG_BLURBS[slug] && (
          <section className="py-16 bg-white border-b border-slate-200" data-testid="seo-longform">
            <div className="max-w-3xl mx-auto px-6">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0a2540] tracking-tight mb-6">
                {entry.type === "brand" && `About ${entry.brand} TV Repairs in Bangalore`}
                {entry.type === "service" && `More About ${entry.name}`}
                {entry.type === "locality" && `Why ${entry.area} Homes Trust TV CARE`}
              </h2>
              <p className="text-base text-slate-700 leading-relaxed">
                {LONG_BLURBS[slug]}
              </p>
            </div>
          </section>
        )}

        {/* BRAND FAQs (only on brand landing pages) */}
        {faqs && (
          <section className="py-16 bg-[#f8fafc] border-b border-slate-200" data-testid="seo-brand-faqs">
            <div className="max-w-3xl mx-auto px-6">
              <span className="text-[#f97316] text-xs font-extrabold uppercase tracking-widest block mb-2">Good to know</span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0a2540] tracking-tight mb-8">
                {entry.brand} TV Repair &mdash; Your Questions Answered
              </h2>
              <div className="space-y-4">
                {faqs.map((f, i) => (
                  <details key={i} data-testid={`brand-faq-${i}`} className="group bg-white border border-slate-200 rounded-xl p-6 transition-all">
                    <summary className="flex justify-between items-center cursor-pointer font-bold text-[#0a2540] list-none">
                      <span className="flex items-center gap-3">
                        <HelpCircle className="h-5 w-5 text-[#f97316] flex-shrink-0" /> {f.question}
                      </span>
                      <ChevronRight className="h-5 w-5 text-slate-400 transition-transform group-open:rotate-90 flex-shrink-0" />
                    </summary>
                    <p className="text-sm text-slate-600 leading-relaxed mt-4 pl-8">{f.answer}</p>
                  </details>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* WHY US */}
        <section className="py-16 bg-[#0a2540] text-white">
          <div className="max-w-7xl mx-auto px-6 text-left">
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-8">Why Choose TV CARE</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {WHY.map((f, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <div className="p-3 bg-[#f97316]/15 w-fit rounded-xl text-[#f97316] mb-4">
                    <f.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-bold text-base mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-300 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* INTERLINKS */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 text-left space-y-10">
            <div>
              <h2 className="text-sm font-extrabold text-[#0a2540] uppercase tracking-wider mb-4 flex items-center gap-2">
                <Wrench className="h-4 w-4 text-[#f97316]" /> Other Repair Services
              </h2>
              <div className="flex flex-wrap gap-2.5">
                {relatedServices.map((s, i) => (
                  <Link key={i} to={`/${s.slug}`} className="inline-flex items-center bg-[#f8fafc] border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700 hover:border-[#f97316] hover:text-[#0a2540] transition-all">
                    {s.name}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-sm font-extrabold text-[#0a2540] uppercase tracking-wider mb-4 flex items-center gap-2">
                <Tv className="h-4 w-4 text-[#f97316]" /> Brands We Service
              </h2>
              <div className="flex flex-wrap gap-2.5">
                {relatedBrands.map((b, i) => (
                  <Link key={i} to={`/${b.slug}`} className="inline-flex items-center bg-[#f8fafc] border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700 hover:border-[#f97316] hover:text-[#0a2540] transition-all">
                    {b.brand}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#0a2540]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight mb-3">Ready to Watch Again?</h2>
            <p className="text-base text-slate-300 mb-8 max-w-2xl mx-auto">
              Give us a ring today for a friendly chat and a fair quote — same-day doorstep visits across Bangalore.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8884340007" onClick={() => { gtagEvent("call_click", { location: "seo-cta" }); gtagConversion("call"); }} className="bg-[#f97316] hover:bg-[#ea580c] text-white rounded-xl h-12 px-8 text-base font-semibold transition-all flex items-center justify-center gap-2">
                <Phone className="h-5 w-5" /> Call (888) 434-0007
              </a>
              <Button onClick={() => navigate("/?action=book")} className="bg-white hover:bg-slate-100 text-[#0a2540] rounded-xl h-12 px-8 text-base font-semibold transition-all flex items-center justify-center gap-2">
                Request a Visit <ArrowRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="bg-[#0a2540] text-white border-t border-slate-800 py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row justify-between items-center gap-4 text-xs text-slate-400">
          <p>© 2026 TV CARE. All rights reserved.</p>
          <Link to="/" className="hover:underline">Back to Home</Link>
        </div>
      </footer>
    </div>
  );
}
